<template>
  <section class="header">
    <div class="header__status">
      <UtilityStatusBar :status="appStatus" />
    </div>

    <div class="header__top">
      <div class="header__top--wrap">
        <AppNavigation />
      </div>
    </div>

    <div class="header__bottom">
      <div class="header__bottom--wrap">
        <div class="header__search">
          <FormSearchInput @submitted="searchSubmitted" />
        </div>

        <div class="header__selection">
          <UtilityFilterSelection
            :key="selectionKey"
            @selectionClosed="updateSelectionKey()"
          />
        </div>

        <div
          v-if="totalSelectedFilters === 0"
          class="header__info"
        >
          <UtilityTips />
        </div>

        <div
          v-else
          class="header__filters"
        >
          <UtilityQueryExplanator />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import AppNavigation from '@/components/app/Navigation.vue'
import FormSearchInput from '@/components/form/SearchInput.vue'
import FormButton from '@/components/form/Button.vue'
import UtilityQueryExplanator from '@/components/utility/QueryExplanator.vue'
import UtilityFilterSelection from '@/components/utility/FilterSelection.vue'
import UtilityStatusBar from '@/components/utility/StatusBar.vue'
import UtilityTips from '@/components/utility/Tips.vue'
import helperMethods from '@/helpers/utility.js'

export default {
  components: {
    AppNavigation,
    FormSearchInput,
    FormButton,
    UtilityQueryExplanator,
    UtilityFilterSelection,
    UtilityStatusBar,
    UtilityTips
  },
  data () {
    return {
      selectionKey: 0
    }
  },
  computed: {
    appLoading () {
      return this.$store.getters['app/getAppLoading']
    },
    appStatus () {
      return this.$store.getters['app/getAppStatus']
    },
    searchInput () {
      return this.$store.getters['search/getInput']
    },
    selectedFilters () {
      return this.$store.getters['search/getSelectedFilters']
    },
    selectingFilter () {
      return this.$store.getters['search/getSelectingFilter']
    },
    totalSelectedFilters () {
      let total = 0

      if (this.selectedFilters) {
        for (const [key, value] of Object.entries(this.selectedFilters)) {
          if (this.selectedFilters[key] && this.selectedFilters[key].length > 0) {
            total += this.selectedFilters[key].length
          }
        }
      }

      return total
    }
  },
  methods: {
    searchSubmitted () {
      if (this.searchInput.length >= 2) {
        let view = 'search'
        let views = ['search', 'datasets', 'pdfs', 'news']
        let filters = {
          'queries': [{
            'type': 'queries',
            'id': this.searchInput.trim(),
            'attributes': {
              'name': this.searchInput.trim(),
              'condition': false
            }
          }]
        }

        if (views.includes(this.$route.name.toLowerCase())) {
          view = this.$route.name.toLowerCase()
        }

        this.$store.dispatch('app/setSettings', { 'name': 'datasets.access', 'value': 'all' }) // TODO: Create app/resetState|Settings function?
        this.$store.dispatch('app/setSettings', { 'name': 'datasets.sort', 'value': 'relevance' })
        this.$store.dispatch('app/setSettings', { 'name': 'pdfs.access', 'value': 'all' })
        this.$store.dispatch('app/setSettings', { 'name': 'pdfs.sort', 'value': 'relevance' })
        this.$store.dispatch('app/setSettings', { 'name': 'news.access', 'value': 'all' })
        this.$store.dispatch('app/setSettings', { 'name': 'news.sort', 'value': 'relevance' })
        this.$store.dispatch('app/setSettings', { 'name': 'news.before_id', 'value': false })
        this.$store.dispatch('app/setSettings', { 'name': 'news.after_id', 'value': false })
        this.$store.dispatch('search/resetState')
        this.$store.dispatch('search/setInput', '')
        this.$store.dispatch('search/setSelectedFilters', filters)

        this.$router.push('/' + view + '?' + this.$store.getters['search/getFiltersQuery'](false, false))
      }
    },
    updateSelectionKey () {
      this.selectionKey += 1
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.header {
  @include font('content--md', $white, 0.9);
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: auto;

  &__status {
    position: absolute;
    z-index: 102;
    width: 100%;
    height: 3px;
    background-color: rgba($bg-1, 0);
    transition: background-color 0.2s ease-in-out;
  }

  &__top {
    position: fixed;
    z-index: 101;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 40px;
    background-color: $bg-2;
    border-bottom: 1px solid $black;

    @media screen and (min-width: $breakpointLg) {
      height: 44px;
    }

    &--wrap {
      display: flex;
      align-items: center;
      width: calc(100% - 16px);
      max-width: $xlContentWidth;
      margin: 0 auto;

      @media screen and (min-width: $breakpointSm) {
        width: calc(100% - 32px);
      }
    }
  }

  &__bottom {
    position: fixed;
    z-index: 100;
    top: 41px;
    left: 0px;
    width: 100%;
    height: 84px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $bg-4;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 2px solid $black;
    box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
    user-select: none;

    @media screen and (min-width: $breakpointSm) {
      top: 41px;
      height: 40px;
    }

    @media screen and (min-width: $breakpointLg) {
      top: 45px;
      height: 44px;
    }

    &--noshadow {
      box-shadow: none;
    }

    &--wrap {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      width: 100%;
      max-width: $xlContentWidth;
      margin: 0 auto;

      @media screen and (min-width: $breakpointSm) {
        width: calc(100% - 32px);
        flex-wrap: nowrap;
      }
    }
  }

  &__search {
    flex: 1;
    width: auto;
    min-width: 200px;
    padding: 0px 0px 4px 8px;
    border-bottom: 1px solid $black;

    @media screen and (min-width: $breakpointSm) {
      width: 320px;
      min-width: 320px;
      padding: 0px;
      border-bottom: none;
    }

    @media screen and (min-width: $breakpointLg) {
      width: 380px;
      min-width: 380px;
    }
  }

  &__selection {
    width: 106px;
    min-width: 106px;
    padding: 0px 8px 4px 6px;
    border-bottom: 1px solid $black;

    @media screen and (min-width: $breakpointSm) {
      padding-bottom: 0px;
      border-bottom: none;
    }

    @media screen and (min-width: $breakpointLg) {
      width: 116px;
      min-width: 116px;
    }
  }

  &__filters {
    flex-basis: 100%;
    width: 100%;
    margin: 0px;
    padding-top: 2px;
    border-top: 1px solid rgba($white, 0.1);

    @media screen and (min-width: $breakpointSm) {
      width: calc(100% - 444px);
      margin: 0 0 0 6px;
      padding-top: 0px;
      border-top: none;
    }

    @media screen and (min-width: $breakpointLg) {
      width: calc(100% - 514px);
    }
  }

  &__info {
    width: 100%;
    border-top: 1px solid rgba($white, 0.1);

    @media screen and (min-width: $breakpointSm) {
      border-top: none;
    }
  }
}

.filterInfo {
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    .filterInfo__text {
      color: $white;
      border-bottom: 1px dashed $blue;
    }

    .filterInfo__icon {
      fill: rgba($white, 0.9);
    }
  }

  &__icon {
    width: 16px;
    height: 16px;
    margin-right: 8px;
    fill: rgba($white, 0.5);
    transition: fill 0.2s ease-out;

    @media screen and (min-width: $breakpointLg) {
      width: 14px;
      height: 14px;
    }
  }

  &__text {
    color: rgba($white, 0.7);
    //text-transform: uppercase;
    //font-size: 14px;
  }
}

</style>
